import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ShareIcon from 'public/icons/icn-ios-share.svg';
import MoreVertIcon from 'public/icons/icn-more-vert.svg';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductService from 'services/product.service';
import ShopService from 'services/shop.service';
import UserService from 'services/user.service';
import { ProductType } from 'types/product.types';
import { ShopType } from 'types/shop.types';
import { UserType } from 'types/user.types';
import { notification } from 'utils/snackbar';

import DeleteProductDialog from '../DeleteProductDialog/DeleteProductDialog';
import HideProductDialog from '../HideProductDialog/HideProductDialog';
import ReportDialog, { ReportData } from '../ReportDialog/ReportDialog';

export type OptionedMenuItem = {
  name: string;
  actionType: string;
};

type OptionedMenuPropsType = {
  user?: UserType;
  targetUser: UserType;
  targetShop: ShopType;
  product?: ProductType;
  refreshParentDataFn?: () => void;
  onClickShare?: () => void;
};

enum ActionTypes {
  REPORT = 'report',
  BLOCK = 'block',
  UNBLOCK = 'unblock',
  PIN = 'pin',
  UNPIN = 'unpin',
  HIDE = 'hide',
  DELETE = 'delete',
  TOGGLE_APPROPRIATE_PRODUCTS = 'toggle_appropriate_products',
}

const OptionedMenu = (props: OptionedMenuPropsType) => {
  const { t } = useTranslation();
  const { user, product, targetUser, targetShop, refreshParentDataFn, onClickShare } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [reportData, setReportData] = useState<ReportData>(null);
  const [reportDialogOpened, setReportDialogOpened] = useState<boolean>(false);
  const [deleteProductDialogOpened, setDeleteProductDialogOpened] = useState<boolean>(false);
  const [hideProductDialogOpened, setHideProductDialogOpened] = useState<boolean>(false);
  const productService = new ProductService();
  const shopService = new ShopService();
  const userService = new UserService();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReport = () => {
    const data: ReportData = {
      reporter: {
        id: user?.id,
        shopId: user?.shop?.id,
        shopSlug: user?.shop?.activeSlug || user?.shop?.active_slug || '',
      },
      reported: {
        id: targetUser?.id,
        shopId: targetShop?.id,
        productId: product?.id,
        shopSlug: targetShop?.activeSlug || targetShop?.active_slug || '',
      },
      reason: null,
      comment: '',
    };

    setReportData(data);
    setReportDialogOpened(true);
  };

  const blockUser = async () => {
    await userService
      .blockUser(targetUser?.id, { silentFail: true })
      .then(() => {
        if (typeof refreshParentDataFn === 'function') {
          refreshParentDataFn();
        }
        notification.showSuccessNotification(t('components.report.block.success'));
      })
      .catch((error) => {
        console.error(error);

        if (error?.data?.data?.type === 'CANNOT_BLOCK_FOR_48H') {
          return notification.showSuccessNotification(error?.data?.data?.msg, {
            preventDuplicate: true,
          });
        }

        return notification.showErrorNotification(
          { error },
          {
            preventDuplicate: true,
          },
        );
      });
  };

  const unBlockUser = async () => {
    await userService
      .unBlockUser(targetUser?.id)
      .then(() => {
        if (typeof refreshParentDataFn === 'function') {
          refreshParentDataFn();
        }
        notification.showSuccessNotification(t('components.report.unblock.success'));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const pinProduct = async () => {
    await productService
      .pinningProduct(product.id)
      .then(() => {
        product.pinnedAt = true;

        if (typeof refreshParentDataFn === 'function') {
          refreshParentDataFn();
        }
        notification.showSuccessNotification(t('components.admin.notification.pin'));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const removeProductPin = async () => {
    await productService
      .removingProductPin(product.id)
      .then(() => {
        product.pinnedAt = false;

        if (typeof refreshParentDataFn === 'function') {
          refreshParentDataFn();
        }
        notification.showSuccessNotification(t('components.admin.notification.remove-pin'));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // Admin-only: hide or unhide other users' products
  // Does NOT update my-shop Redux state since it operates on other users' products
  const hideProduct = async () => {
    if (product.status === 'published') {
      return setHideProductDialogOpened(true);
    }

    await productService
      .hideProduct(product.id, {
        statusParams: {
          hideComment: null,
          hideReason: null,
        },
        hide: false,
      })
      .then(() => {
        product.status = 'published';

        if (typeof refreshParentDataFn === 'function') {
          refreshParentDataFn();
        }
        notification.showSuccessNotification(t('components.admin.product.reason.make-visible-success'));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const deleteProduct = async () => {
    setDeleteProductDialogOpened(true);
  };

  const toggleAppropriateProducts = async () => {
    await shopService
      .toggleAppropriateProducts(targetShop.id, {
        appropriateProducts: !targetShop.appropriateProducts,
      })
      .then(() => {
        targetShop.appropriateProducts = !targetShop.appropriateProducts;

        if (typeof refreshParentDataFn === 'function') {
          refreshParentDataFn();
        }
        notification.showSuccessNotification(t('components.admin.notification.appropriate_products_toggled'));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleAction = (actionType: string) => {
    if (actionType === ActionTypes.REPORT) {
      handleReport();
    } else if (actionType === ActionTypes.BLOCK) {
      blockUser();
    } else if (actionType === ActionTypes.UNBLOCK) {
      unBlockUser();
    } else if (actionType === ActionTypes.PIN) {
      pinProduct();
    } else if (actionType === ActionTypes.UNPIN) {
      removeProductPin();
    } else if (actionType === ActionTypes.HIDE) {
      hideProduct();
    } else if (actionType === ActionTypes.DELETE) {
      deleteProduct();
    } else if (actionType === ActionTypes.TOGGLE_APPROPRIATE_PRODUCTS) {
      toggleAppropriateProducts();
    }

    handleClose();
  };

  const getOptions = () => {
    const options = [{ name: t('components.report.title'), actionType: ActionTypes.REPORT }];

    if (targetUser?.id) {
      if (targetShop.isBlocked && targetShop.isBlockedByCurrentUser) {
        options.push({
          name: t('components.report.unblock.title'),
          actionType: ActionTypes.UNBLOCK,
        });
      } else {
        options.push({
          name: t('components.report.block.title'),
          actionType: ActionTypes.BLOCK,
        });
      }
    }

    if (user?.is_admin) {
      if (targetShop.appropriateProducts) {
        options.push({
          name: t('components.admin.product.mark-shop-inappropriate'),
          actionType: ActionTypes.TOGGLE_APPROPRIATE_PRODUCTS,
        });
      } else {
        options.push({
          name: t('components.admin.product.mark-shop-appropriate'),
          actionType: ActionTypes.TOGGLE_APPROPRIATE_PRODUCTS,
        });
      }

      if (product) {
        if (product.pinnedAt) {
          options.push({
            name: t('components.admin.product.remove-pin'),
            actionType: ActionTypes.UNPIN,
          });
        } else {
          options.push({
            name: t('components.admin.product.pin'),
            actionType: ActionTypes.PIN,
          });
        }

        options.push(
          {
            name: product.status === 'published' ? t('components.admin.product.hide') : t('components.admin.product.make-visible'),
            actionType: ActionTypes.HIDE,
          },
          {
            name: t('components.admin.product.delete'),
            actionType: ActionTypes.DELETE,
          },
        );
      }
    }

    return options;
  };

  return (
    <>
      {onClickShare && (
        <IconButton aria-label="share" aria-controls="share-menu" aria-haspopup="true" onClick={onClickShare}>
          <ShareIcon />
        </IconButton>
      )}
      {user?.id && (
        <IconButton aria-label="more" aria-controls="optioned-menu" aria-haspopup="true" onClick={handleClick}>
          <MoreVertIcon />
        </IconButton>
      )}
      <Menu
        id="optioned-menu"
        className="optioned-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        {getOptions()?.map((option) => (
          <MenuItem key={option.name} onClick={() => handleAction(option.actionType)} className="optioned-menu__item">
            {option.name}
          </MenuItem>
        ))}
      </Menu>

      <ReportDialog opened={reportDialogOpened} reportData={reportData} close={() => setReportDialogOpened(false)} />

      {user?.is_admin && (
        <>
          <DeleteProductDialog
            opened={deleteProductDialogOpened}
            product={product}
            refreshParentDataFn={refreshParentDataFn}
            close={() => setDeleteProductDialogOpened(false)}
          />
          <HideProductDialog
            opened={hideProductDialogOpened}
            product={product}
            refreshParentDataFn={refreshParentDataFn}
            close={() => setHideProductDialogOpened(false)}
          />
        </>
      )}
    </>
  );
};

export default OptionedMenu;
